import type { ChatMessage } from './types';

/**
 * Monta a bolha da conversa a partir do aviso de tempo real.
 *
 * Morava dentro de `aplicaMensagem` (hooks.ts), fechada numa closure e portanto
 * impossível de testar sem montar o hook inteiro. Saiu daqui pra fora justamente
 * porque um defeito passou despercebido nela: campos que o servidor mandava eram
 * jogados fora na montagem, e o único teste que existia exercitava uma RÉPLICA
 * da regra — passava com o código real quebrado.
 */

/** O que os workers mandam no `message.received`. Tudo opcional de propósito:
 *  são três emissores (Cloud API, coexistência, canal QR) com payloads que
 *  cresceram em épocas diferentes. */
export interface EventoDeMensagem {
  message_id?: number | null;
  wamid?: string | null;
  type: string;
  body: string | null;
  direction?: 'inbound' | 'outbound';
  created_at?: string;
  received_at?: string;
  media_id?: number | null;
  media_mime?: string | null;
  media_status?: ChatMessage['media_status'];
  media_waveform?: number[] | null;
  media_duration_ms?: number | null;
  media_width?: number | null;
  media_height?: number | null;
  media_thumbnail_url?: string | null;
  media_original_name?: string | null;
  sent_by_user_id?: number | null;
  sent_by_user_name?: string | null;
  reply_to_wamid?: string | null;
  reply_to?: ChatMessage['reply_to'];
  status_reply?: ChatMessage['status_reply'];
  /** Agrupador: prazo do pacote ainda aberto (ISO). É o que faz a contagem
   *  aparecer ao lado do balão. */
  batch_open_until?: string | null;
  /** Agrupador inteligente: relógio parado neste restante (ms); null = contando. */
  batch_held_remaining_ms?: number | null;
  /** Agrupador: o pacote juntou 2+ textos (selo). */
  batched?: boolean;
  /** A IA reescreveu o pacote (selo). */
  ai_rewritten?: boolean;
  /** O que o atendente escreveu antes da IA (hover do selo). Vem junto no
   *  evento — sem ele o selo nascia sem conteúdo e o original só aparecia
   *  depois de um F5, em qualquer canal. */
  ai_original?: string | null;
  /** Prévia estruturada do template HSM (header/body/botões) — o MessageBubble
   *  renderiza template POR ELA (+ media_id pra mídia do header). Sem mapear,
   *  a bolha ao vivo de um disparo/composer de template nasce só com o texto
   *  e a estrutura aparece apenas no F5 (mesma classe do reloginho). */
  template_preview?: ChatMessage['template_preview'];
  internal_note?: boolean;
  /**
   * Estado de entrega, quando o evento já nasce sabendo.
   *
   * Vale pra mensagem que o DONO manda pelo celular e o painel espelha,
   * nos dois canais: coexistência (`smb-message-echoes.ts`) manda
   * `status_rank: 1`; canal QR (`inbound.go`, `source='other_device'`) manda
   * `2`. Os dois mandam `sent_at`, e a coexistência ainda manda
   * `delivered_at`/`read_at` quando o status chegou antes da inserção.
   */
  status_rank?: number;
  sent_at?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  /** Saída paga (Cloud oficial num canal com o selo ligado) — pra a bolha ao
   *  vivo já nascer com o selo "Cobrada" sem F5. */
  billable?: boolean;
  /** Coexistência: o worker marca o eco do celular do lojista com este source. */
  source?: string;
}

export function montaBolhaDoEvento(evt: EventoDeMensagem, agora = new Date()): ChatMessage {
  return {
    // Sem id (evento antigo/parcial), inventa um negativo — o negativo é a
    // convenção de "ainda não confirmada" usada pelo dedup lá em cima.
    id: evt.message_id ?? Math.floor(-Math.random() * 1e9),
    // O wamid vem no evento e o botao Responder depende dele (MessageBubble:
    // canReply = Boolean(message.wamid)). Antes era `null` fixo — a mensagem
    // que chegava por realtime nao mostrava "Responder" ate um F5 trazer o
    // wamid do servidor. Mesma classe do reloginho: campo que o servidor manda,
    // jogado fora na montagem.
    wamid: evt.wamid ?? null,
    direction: evt.direction ?? 'inbound',
    type: evt.type,
    body: evt.body,
    media_id: evt.media_id ?? null,
    media_mime: evt.media_mime ?? null,
    // Worker decide: Cloud API manda 'pending' COM media_id (row pre-criada),
    // whatsmeow manda 'pending' SEM media_id (download async, media.ready patcha).
    media_status: evt.media_status ?? null,
    media_waveform: evt.media_waveform ?? null,
    media_duration_ms: evt.media_duration_ms ?? null,
    media_width: evt.media_width ?? null,
    media_height: evt.media_height ?? null,
    media_thumbnail_url: evt.media_thumbnail_url ?? null,
    media_original_name: evt.media_original_name ?? null,
    /**
     * Estado de entrega: usa o que o evento trouxe.
     *
     * Estes quatro eram `0`/`null` FIXOS, e isso travava no reloginho toda
     * mensagem que o dono mandava pelo celular — nos dois canais, embora os
     * dois workers já mandassem `status_rank` e `sent_at` no aviso. O banco
     * ficava certo (por isso o F5 resolvia); só a bolha ao vivo nascia sem
     * saber que a mensagem já tinha saído.
     *
     * Por que demorou a aparecer: enquanto a conversa vencia de 30 em 30s,
     * qualquer remontagem rebuscava e consertava sozinha. Sem o vencimento por
     * relógio, o que o evento traz é o que fica.
     *
     * Por que TRANSFERIR o atendimento "consertava": transferir invalida a
     * conversa (`onTicketChanged`), então a próxima olhada rebusca do servidor
     * e mostra o traço certo. Mascarava, não consertava — e foi essa diferença
     * ("transfiro e aparece normal") que entregou o diagnóstico.
     *
     * `?? 0` / `?? null` preserva o comportamento de quem não manda os campos:
     * mensagem que ENTRA (não tem estado de entrega) e o eco do nosso próprio
     * envio, que segue esperando a confirmação da Meta.
     */
    status_rank: evt.status_rank ?? 0,
    sent_at: evt.sent_at ?? null,
    delivered_at: evt.delivered_at ?? null,
    read_at: evt.read_at ?? null,
    failed_at: null,
    error_title: null,
    created_at: evt.created_at ?? evt.received_at ?? agora.toISOString(),
    sent_by_user_id: evt.sent_by_user_id ?? null,
    billable: evt.billable ?? false,
    // Eco do celular do lojista (coexistência): a bolha já nasce marcada, senão
    // o selo só apareceria no F5.
    ...(evt.source === 'smb_app' && { from_phone: true }),
    /**
     * Agrupador e IA: mesma história do selo acima, e o mesmo remédio.
     *
     * Sem estes três, a bolha que nasce PELO AVISO (e não por já estar em
     * cache) vinha sem o prazo do pacote — então a contagem ao lado do balão
     * simplesmente não aparecia. Como depende de quem chega primeiro, o aviso
     * ou a resposta do envio, o defeito era intermitente: às vezes contava,
     * às vezes não, sem nada de diferente no servidor (medido: o pacote abria
     * e esperava os segundos configurados nas duas situações).
     */
    ...(evt.batch_open_until !== undefined && { batch_open_until: evt.batch_open_until }),
    ...(evt.batch_held_remaining_ms !== undefined && {
      batch_held_remaining_ms: evt.batch_held_remaining_ms,
    }),
    ...(evt.batched === true && { batched: true }),
    ...(evt.ai_rewritten === true && { ai_rewritten: true }),
    ...(typeof evt.ai_original === 'string' && { ai_original: evt.ai_original }),
    ...(evt.reply_to_wamid && { reply_to_wamid: evt.reply_to_wamid }),
    ...(evt.reply_to && { reply_to: evt.reply_to }),
    ...(evt.template_preview && { template_preview: evt.template_preview }),
    ...(evt.status_reply && { status_reply: evt.status_reply }),
    ...(evt.internal_note && { internal_note: true }),
    ...(evt.sent_by_user_name && { sent_by_user_name: evt.sent_by_user_name }),
  };
}
